import React, { useState, useEffect, useRef } from 'react';
import Map, { Source, Layer } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Search, X, Loader2, ChevronDown, Globe } from 'lucide-react';
import { BASEMAPS } from '../../../data/mockData';
import { computeDiscreteColorBands, formatValue } from '../../../utils/mapUtils';
import { mapApi } from '../../../api/mapApi';
import { getPaletteColors, getDefaultPalette, mapIntervalToColorIndex } from '../../../utils/colorPalettes';
import { cn } from '../../../lib/utils';

// Helper to clamp numeric values to safe range for protobuf varint encoding
const clampToSafeRange = (value) => {
  if (typeof value !== 'number' || !isFinite(value)) {
    return value;
  }
  const MAX_SAFE = Number.MAX_SAFE_INTEGER;
  const MIN_SAFE = -Number.MAX_SAFE_INTEGER;

  if (value > MAX_SAFE) return MAX_SAFE;
  if (value < MIN_SAFE) return MIN_SAFE;
  return value;
};

// Helper to build filter condition expression for MapLibre
const buildFilterCondition = (valueFilter) => {
  if (!valueFilter) return null;

  // Clamp filter values to safe range to prevent protobuf varint encoding errors
  const safeMin = typeof valueFilter.min === 'number' ? clampToSafeRange(valueFilter.min) : valueFilter.min;
  const safeMax = typeof valueFilter.max === 'number' ? clampToSafeRange(valueFilter.max) : valueFilter.max;

  const isExactMatch = safeMin === safeMax;
  const upperBound = (isExactMatch || valueFilter.isLast)
    ? ['<=', ['get', 'value'], safeMax]
    : ['<', ['get', 'value'], safeMax];

  return isExactMatch
    ? ['==', ['get', 'value'], safeMin]
    : ['all', ['>=', ['get', 'value'], safeMin], upperBound];
};

// MapLibre expression for discrete color bands (no interpolation)
// Uses the exact same color bands as the legend
const getLayerColor = (layer) => {
  const min = layer.stats?.min ?? 0;
  const max = layer.stats?.max ?? 100;

  // Compute discrete color bands using shared function (same as Colorbar)
  const { stepExpression, useLinear, minColor, maxColor } = computeDiscreteColorBands(
    layer,
    getPaletteColors,
    mapIntervalToColorIndex,
    getDefaultPalette,
    6
  );

  // Handle edge case: when all values are the same, stepExpression is a constant color string
  if (typeof stepExpression === 'string') {
    return stepExpression;
  }

  // If linear interpolation is needed (edge case)
  if (useLinear || !stepExpression) {
    return [
      'interpolate',
      ['linear'],
      ['get', 'value'],
      min, minColor || '#440154',
      max, maxColor || '#440154'
    ];
  }

  // Return discrete step expression (no interpolation)
  // This uses the exact same colors as the legend
  return stepExpression;
};

const MapPanel = ({
  mapRef,
  mapLayers,
  hoverInfo,
  selectedInfo,
  onMouseMove,
  onMapClick,
  onToggleLayerVisibility,
  onRemoveLayer,
  onUpdateLayerTheme,
  onOpenBuilder
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [, setMapVersion] = useState(0); // Force re-render on map move
  const [selectedBasemap, setSelectedBasemap] = useState('light');
  const [isBasemapMenuOpen, setIsBasemapMenuOpen] = useState(false);
  const basemapMenuRef = useRef(null);

  // Close basemap menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (basemapMenuRef.current && !basemapMenuRef.current.contains(event.target)) {
        setIsBasemapMenuOpen(false);
      }
    };

    if (isBasemapMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBasemapMenuOpen]);

  const handleGeocode = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || isSearching) return;

    setIsSearching(true);
    setSearchError(null);

    try {
      const result = await mapApi.geocode(searchQuery.trim());

      if (mapRef.current && result.latitude && result.longitude) {
        // Use getMap() to access the underlying MapLibre GL map instance
        const map = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
        if (map && typeof map.flyTo === 'function') {
          map.flyTo({
            center: [result.longitude, result.latitude],
            zoom: 10,
            duration: 2500,
            curve: 1.42 // Smooth curve for natural easing (default is 1.42)
          });
        } else if (map && typeof map.easeTo === 'function') {
          // Fallback to easeTo if flyTo is not available - smoother but no curve
          map.easeTo({
            center: [result.longitude, result.latitude],
            zoom: 10,
            duration: 2500
          });
        }
      }

      setSearchQuery('');
    } catch (error) {
      console.error('Geocoding error:', error);
      setSearchError(error.response?.data?.detail || 'Failed to find location');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="h-full w-full bg-white dark:bg-gray-950 relative">
      {/* Basemap Selector */}
      <div className="absolute bottom-4 left-4 z-50 pointer-events-none">
        <div className="relative pointer-events-auto" ref={basemapMenuRef}>
          <button
            onClick={() => setIsBasemapMenuOpen(!isBasemapMenuOpen)}
            className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-lg shadow-md border border-gray-200 dark:border-gray-800 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            <Globe className="w-4 h-4" />
            <span className="font-semibold">{BASEMAPS[selectedBasemap].name}</span>
            <ChevronDown className={cn(
              "w-4 h-4 transition-transform duration-200",
              isBasemapMenuOpen && "rotate-180"
            )} />
          </button>
          {isBasemapMenuOpen && (
            <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 overflow-hidden min-w-[180px]">
              {Object.values(BASEMAPS).map((basemap) => (
                <button
                  key={basemap.id}
                  onClick={() => {
                    setSelectedBasemap(basemap.id);
                    setIsBasemapMenuOpen(false);
                  }}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm font-medium transition-colors",
                    selectedBasemap === basemap.id
                      ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  )}
                >
                  {basemap.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="absolute top-4 right-4 z-50 pointer-events-none">
        <form onSubmit={handleGeocode} className="pointer-events-auto w-64">
          <div className="relative">
            <div className="flex items-center bg-white/40 dark:bg-gray-900/40 backdrop-blur-md rounded-lg shadow-md border border-gray-200/30 dark:border-gray-700/30 overflow-hidden transition-all duration-200 focus-within:bg-white/85 dark:focus-within:bg-gray-900/85 focus-within:border-emerald-400/50 focus-within:shadow-xl focus-within:ring-2 focus-within:ring-emerald-400/20">
              <div className="pl-3 pr-1.5 flex items-center">
                {isSearching ? (
                  <Loader2 className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400 animate-spin" />
                ) : (
                  <Search className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                )}
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchError(null);
                }}
                placeholder="Search for a location..."
                className="flex-1 bg-transparent border-0 outline-none px-2 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-0"
                disabled={isSearching}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setSearchError(null);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  disabled={isSearching}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            {searchError && (
              <div className="mt-2 px-3 py-1.5 bg-red-50/90 dark:bg-red-900/30 border border-red-200/50 dark:border-red-800/50 rounded-lg text-xs text-red-600 dark:text-red-400 backdrop-blur-sm">
                {searchError}
              </div>
            )}
          </div>
        </form>
      </div>

      <Map
        ref={mapRef}
        initialViewState={{ longitude: 45.0, latitude: 25.0, zoom: 4 }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={BASEMAPS[selectedBasemap].style}
        mapLibreGL={maplibregl}
        interactiveLayerIds={mapLayers.filter(l => l.visible).flatMap(l => [`l-${l.id}-fill`, `l-${l.id}-circle` || `l-${l.id}-symbol`])}
        onMouseMove={onMouseMove}
        onClick={onMapClick}
        onRender={() => setMapVersion(v => v + 1)}
      >
        {mapLayers
          .slice()
          .reverse()
          .filter(layer => layer.data && layer.status !== 'loading' && layer.status !== 'error')
          .map((layer) => {
            // Filter data based on valueFilter - only show geometries in selected range
            const data = layer.data;

            // Build filter condition for MapLibre filter property
            const filterCondition = buildFilterCondition(layer.valueFilter);

            // Simple opacity functions - no conditional logic needed since we filter
            const getFillOpacity = () => 0.8;
            const getLineOpacity = () => 0.4;
            const getLineWidth = () => 0.5;
            const getLineColor = () => '#ffffff';

            const isPointLayer = data.features[0] && ['Point', 'MultiPoint'].includes(data.features[0].geometry?.type);

            // Get heatmap color ramp based on layer color scheme
            // Always start from transparent at density 0 to only show heatmap where points exist
            const getHeatmapColor = () => {
              // Get the layer's palette configuration
              const paletteName = layer.config?.palette || getDefaultPalette();
              const isInverted = layer.config?.inverted || false;

              // Get full palette colors
              const paletteColors = getPaletteColors(paletteName, isInverted);

              if (!paletteColors || paletteColors.length === 0) {
                // Fallback to default gradient
                return [
                  'interpolate',
                  ['linear'],
                  ['heatmap-density'],
                  0, 'rgba(0,0,0,0)',
                  0.01, 'rgba(68,1,84,0.1)',
                  0.1, 'rgba(68,1,84,0.3)',
                  0.5, 'rgba(53,183,121,0.7)',
                  1, 'rgba(253,231,37,1)'
                ];
              }

              // Convert hex colors to rgba for transparency control
              const hexToRgba = (hex, alpha) => {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `rgba(${r},${g},${b},${alpha})`;
              };

              // Build gradient using all palette colors
              // Distribute colors across density range from 0.01 to 1.0
              const stops = [0, 'rgba(0,0,0,0)']; // Start transparent

              // Add color stops for each palette color - all with same opacity
              paletteColors.forEach((color, index) => {
                const density = 0.01 + (index / (paletteColors.length - 1)) * 0.99;
                // All colors have the same opacity regardless of density
                const opacity = 1;
                stops.push(density, hexToRgba(color, opacity));
              });

              return [
                'interpolate',
                ['linear'],
                ['heatmap-density'],
                ...stops
              ];
            };

            return (
              <Source key={`s-${layer.id}`} id={`s-${layer.id}`} type="geojson" data={data}>
                <Layer
                  id={`l-${layer.id}-fill`}
                  type="fill"
                  {...(filterCondition ? { filter: filterCondition } : {})}
                  layout={{
                    visibility: (layer.visible && !isPointLayer) ? 'visible' : 'none'
                  }}
                  paint={{
                    'fill-color': getLayerColor(layer),
                    'fill-opacity': getFillOpacity()
                  }}
                />
                {/* Heatmap layer for points when zoomed out */}
                {isPointLayer && (
                  <Layer
                    id={`l-${layer.id}-heatmap`}
                    type="heatmap"
                    {...(filterCondition ? { filter: filterCondition } : {})}
                    layout={{
                      visibility: layer.visible ? 'visible' : 'none'
                    }}
                    paint={{
                      // Weight points by their value if available, otherwise use constant weight
                      'heatmap-weight': layer.stats?.min !== undefined && layer.stats?.max !== undefined
                        ? [
                          'case',
                          ['has', 'value'],
                          [
                            'interpolate',
                            ['linear'],
                            ['get', 'value'],
                            layer.stats.min, 0.1,
                            layer.stats.max, 1
                          ],
                          0.5 // Default weight if value property doesn't exist
                        ]
                        : 0.5, // Default weight if stats are not available
                      // Intensity increases with zoom to maintain visibility
                      'heatmap-intensity': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        0, 1,
                        2, 2,
                        8, 3
                      ],
                      // Color gradient for heatmap based on layer palette
                      'heatmap-color': getHeatmapColor(),
                      // Radius increases with zoom for better coverage
                      'heatmap-radius': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        0, 10,
                        2, 15,
                        8, 20
                      ],
                      // Opacity decreases as zoom increases to blend with points
                      'heatmap-opacity': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        0, 1,
                        4, 0.7,
                        8, 0.4
                      ]
                    }}
                  />
                )}
                {/* Circle layer for points - visible at all zoom levels */}
                <Layer
                  id={`l-${layer.id}-circle`}
                  type="circle"
                  {...(filterCondition ? { filter: filterCondition } : {})}
                  layout={{
                    visibility: (layer.visible && isPointLayer) ? 'visible' : 'none'
                  }}
                  paint={{
                    'circle-color': getLayerColor(layer),
                    'circle-radius': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      0, 2,
                      4, 4,
                      12, 12
                    ],
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#ffffff',
                    // Opacity increases with zoom to blend with heatmap
                    'circle-opacity': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      0, 0.3,
                      4, 0.6,
                      8, getFillOpacity()
                    ]
                  }}
                />
                <Layer
                  id={`l-${layer.id}-line`}
                  type="line"
                  {...(filterCondition ? { filter: filterCondition } : {})}
                  layout={{
                    visibility: (layer.visible && !isPointLayer) ? 'visible' : 'none'
                  }}
                  paint={{
                    'line-color': getLineColor(),
                    'line-width': getLineWidth(),
                    'line-opacity': getLineOpacity()
                  }}
                />
              </Source>
            );
          })}

        {(selectedInfo || hoverInfo) && (() => {
          const info = selectedInfo || hoverInfo;
          const isSelected = !!selectedInfo;

          // Project geographic coordinates to screen space if available
          let tooltipX = info.x;
          let tooltipY = info.y;

          if (info.lngLat && mapRef.current) {
            const map = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
            if (map) {
              const pos = map.project(info.lngLat);
              tooltipX = pos.x;
              tooltipY = pos.y;
            }
          }

          const hoveredLayerId = info.feature.layer.id.replace('l-', '').replace('-fill', '').replace('-circle', '');
          const layer = mapLayers.find(l => l.id === hoveredLayerId);

          let metricLabel = 'Cell Data';
          if (layer?.query?.valueColumn && layer?.query?.aggregation) {
            metricLabel = `${layer.query.aggregation.toUpperCase()} (${layer.query.valueColumn})`;
          } else if (layer?.name) {
            // Strip " by [dataset]" if it was auto-generated previously
            metricLabel = layer.name.split(' by ')[0];
          }

          return (
            <div
              className={cn(
                "absolute z-[70] pointer-events-none bg-white/95 dark:bg-gray-950/95 backdrop-blur-md p-2 rounded-lg shadow-xl border min-w-[140px] transition-all duration-200",
                isSelected
                  ? "border-emerald-500/50 dark:border-emerald-400/50 ring-2 ring-emerald-500/10 dark:ring-emerald-400/10"
                  : "border-gray-100/50 dark:border-gray-800/50"
              )}
              style={{ left: tooltipX, top: tooltipY - 12, transform: 'translate(-50%, -100%)' }}
            >
              <div className="space-y-1.5">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[9px] font-black text-emerald-600 dark:text-emerald-400 tracking-[0.15em] leading-none">
                      {metricLabel}
                    </p>
                    {isSelected && (
                      <div className="px-1 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 rounded text-[6px] font-black text-emerald-700 dark:text-emerald-300 tracking-tighter">
                        Pinned
                      </div>
                    )}
                  </div>
                  <p className="text-base font-black text-gray-900 dark:text-white tracking-tight flex items-baseline gap-1">
                    {formatValue(info.feature.properties.value)}
                  </p>
                </div>

                {Object.entries(info.feature.properties).filter(([key]) => !['h3', 'value', 'geometry', 'id'].includes(key)).length > 0 && (
                  <>
                    <div className="h-px bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800" />
                    <div className="flex flex-col gap-1.5 py-0.5">
                      {Object.entries(info.feature.properties)
                        .filter(([key]) => !['h3', 'value', 'geometry', 'id'].includes(key))
                        .map(([key, val]) => (
                          <div key={key} className="flex flex-col">
                            <span className="text-[7px] text-gray-400 dark:text-gray-500 font-bold tracking-wider leading-none mb-0.5">
                              {key.replace(/_/g, ' ')}
                            </span>
                            <span className="text-[10px] text-gray-700 dark:text-gray-200 font-black tracking-tight">
                              {typeof val === 'number' ? formatValue(val) : String(val)}
                            </span>
                          </div>
                        ))
                      }
                    </div>
                  </>
                )}
              </div>
              {/* Tooltip Arrow */}
              <div className={cn(
                "absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-white/95 dark:bg-gray-950/95 border-b border-r rotate-45 transition-colors",
                isSelected
                  ? "border-emerald-500/50 dark:border-emerald-400/50"
                  : "border-gray-100/50 dark:border-gray-800/50"
              )} />
            </div>
          );
        })()}
      </Map>

      {/* Empty state message when no layers exist - Add Layer button hidden */}
      {
        mapLayers.filter(l => l.id !== 'preview-layer').length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            {/* Add Layer button hidden */}
          </div>
        )
      }
    </div >
  );
};

export default MapPanel;
